import { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import AccordionCard from '@/components/AccordionCard'
import MasterDataTable from '@/components/MasterDataTable/index'
import IconInfoEmpty from '@/assets/svgs/Icon-info.svg?react'
import { fromSchemaShapeAll, sortRows, uploadColumns, uploadHeaders } from '../upload-payload'

// 100 a page, matching pitstop's preview of the same rows (CTD-12795): an upload
// runs to hundreds of rows, and ten a page turns reading one into a page-by-page
// crawl.
const PAGE_LIMIT = 100

/**
 * An uploaded submittal's rows, read-only, in the same collapsible card the eform
 * renders in.
 *
 * A data element submitted by spreadsheet stores its payload as an array of
 * row-records, a shape the eform cannot bind — it renders every field blank. The
 * rows go to a table instead, with the columns the schema titles them by.
 *
 * The card is AccordionCard, as the eform's is, so a long upload can be collapsed
 * out of the way to reach the approve/reject bar. pitstop renders its equivalent
 * bare, without a card; the card is what the design asks for here.
 *
 * Paginated here rather than by the API: the whole payload arrives with the
 * submittal in one response, so there is nothing to fetch per page.
 */
const UploadedRowsTable = ({ title, fileName, payload, schema }) => {
  const [currentPage, setCurrentPage] = useState(1)
  // No default sort: the rows start in the order they were uploaded.
  const [sortConfig, setSortConfig] = useState({ key: null, ascending: true })

  const uploadedRows = useMemo(() => fromSchemaShapeAll(payload, schema), [payload, schema])
  const columns = useMemo(
    () => uploadColumns(uploadHeaders(uploadedRows), schema),
    [uploadedRows, schema]
  )
  // Sorted separately from the upload order above, which still decides the
  // column order — a sort changes which rows come first, not what the table
  // treats as its first row.
  const rows = useMemo(
    () => sortRows(uploadedRows, sortConfig.key, sortConfig.ascending),
    [uploadedRows, sortConfig]
  )

  // A new payload is a different submittal's rows, so the page and sort held
  // for the previous one mean nothing — and a shorter payload would leave the
  // table blank with no way back to a page that exists.
  useEffect(() => {
    setCurrentPage(1)
    setSortConfig({ key: null, ascending: true })
  }, [payload])

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_LIMIT))
  const visibleRows = useMemo(
    () => rows.slice((currentPage - 1) * PAGE_LIMIT, currentPage * PAGE_LIMIT),
    [rows, currentPage]
  )

  const handleSort = key =>
    setSortConfig(prev => ({ key, ascending: prev.key === key ? !prev.ascending : true }))

  return (
    <AccordionCard
      className="mt-6"
      // The eform card's own ceiling. A full page of rows plus header and
      // pagination clears AccordionCard's default 1000px, which would clip the
      // last rows with no scrollbar to recover them.
      contentClassName="max-h-[10000px]"
      title={
        <div data-testid="uploaded-rows-title" className="type-header-4 font-[900]">
          {title}
        </div>
      }
      defaultOpen
      // The table is the card's body, so the card draws the only edge: its padding
      // would otherwise inset the rows 28px on top of the cells' own 24px, and the
      // rows have less room for columns than the card actually has. Matches
      // pitstop's own fix for the same table (CTD-12403).
      flushContent
    >
      {/* Keeps its own inset, which the flush body above gave up: this is a line of
          text in the card, not part of the table's edge-to-edge grid. */}
      {fileName && (
        <div
          className="font-avenir-heavy type-body-2 mt-6 mb-4 px-4 phone:px-7"
          data-testid="uploaded-file-name"
        >
          {fileName}
        </div>
      )}

      {/* `bare`, since the card around it is the accordion's — the table drawing
          its own would put a rounded border inside a rounded border. The card's
          `flushContent` above is the other half: together they leave exactly one
          card edge around the rows. */}
      <MasterDataTable
        bare
        columns={columns}
        data={visibleRows}
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={rows.length}
        pageLimit={PAGE_LIMIT}
        onPageChange={setCurrentPage}
        onSort={handleSort}
        sortColumn={sortConfig.key}
        sortOrder={sortConfig.ascending ? 'asc' : 'desc'}
        emptyMessage="This submission has no rows to display."
        emptyIcon={<IconInfoEmpty width={32} height={32} />}
      />
    </AccordionCard>
  )
}

UploadedRowsTable.propTypes = {
  title: PropTypes.string,
  // The uploaded spreadsheet's own name. Not stored against the submittal today,
  // so the line is left out rather than filled with a stand-in.
  fileName: PropTypes.string,
  // The payload as the API returned it: an array of row-records.
  payload: PropTypes.array,
  schema: PropTypes.object,
}

export default UploadedRowsTable
